import { useState, useEffect } from 'react';
import { BatteryWarning, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const DISMISS_KEY = 'flowupy_android_battery_banner_dismissed';

function isAndroid() {
  return /android/.test(window.navigator.userAgent.toLowerCase());
}

const STR = {
  tr: {
    title: 'Bildirimler gecikebilir/gelmeyebilir',
    desc: 'Telefonunuzun pil tasarrufu ayarları, uygulama kapalıyken bildirimleri engelleyebilir. Tarayıcı uygulamasını (Chrome vb.) pil optimizasyonundan muaf tutmanızı öneririz:',
    steps: 'Ayarlar → Uygulamalar → Chrome → Pil → Kısıtlama Yok', close: 'Kapat',
  },
  en: {
    title: 'Notifications may be delayed or missed',
    desc: 'Your phone’s battery-saver settings can block notifications while the app is closed. We recommend exempting your browser (Chrome etc.) from battery optimization:',
    steps: 'Settings → Apps → Chrome → Battery → No restrictions', close: 'Close',
  },
};

/**
 * On Android, aggressive OEM battery-savers can suspend the browser process in
 * the background, preventing push events. This banner tells the user how to
 * exempt the browser.
 */
export const AndroidBatteryBanner = () => {
  const { language } = useLanguage();
  const L = STR[language] || STR.tr;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(DISMISS_KEY) === 'true';
    if (isAndroid() && !dismissed) {
      const timer = setTimeout(() => setVisible(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, 'true');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border shadow-lg p-4"
      data-testid="android-battery-banner"
    >
      <div className="max-w-lg mx-auto flex items-start gap-3">
        <BatteryWarning className="w-5 h-5 mt-0.5 shrink-0 text-amber-500" />
        <div className="flex-1 text-sm">
          <p className="font-semibold mb-1">{L.title}</p>
          <p className="text-muted-foreground">
            {L.desc}
          </p>
          <p className="text-muted-foreground mt-1 font-medium">
            {L.steps}
          </p>
        </div>
        <button
          onClick={dismiss}
          className="p-1 text-muted-foreground hover:text-foreground"
          data-testid="android-battery-banner-dismiss"
          aria-label={L.close}
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default AndroidBatteryBanner;
